/**
 * Client-side file validation for image resolution and video bitrate.
 *
 * scio1 equivalents:
 * - FileService.getImgDimension() — checks if image > 6144x6144
 * - FileService.getBitRateVideo() — checks if video > 85 Mbps
 *
 * These checks run BEFORE uploading to Transloadit to prevent
 * wasting bandwidth on files that will be rejected.
 */

const MAX_IMAGE_DIMENSION = 6144;
const MAX_VIDEO_BITRATE_MBPS = 85;
const BITS_PER_BYTE = 8;
const MEGABITS_DIVISOR = 1_000_000;

export interface FileValidationError {
  file: File;
  reason: 'resolution' | 'bitrate';
  message: string;
}

/**
 * Check if an image file exceeds the maximum allowed resolution (6144x6144).
 * Returns a promise that resolves to a validation error or null.
 *
 * Only validates image files (MIME type starts with 'image/').
 * Skips SVG files (vector, no pixel dimensions).
 */
export function validateImageResolution(file: File): Promise<FileValidationError | null> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width > MAX_IMAGE_DIMENSION || img.height > MAX_IMAGE_DIMENSION) {
        resolve({
          file,
          reason: 'resolution',
          message: `We only support ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION}(6Kx6K) or below resolution images.`,
        });
      } else {
        resolve(null);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      // If we can't read the image, let it through — the server will reject if needed
      resolve(null);
    };

    img.src = url;
  });
}

/**
 * Check if a video file exceeds the maximum allowed bitrate (85 Mbps).
 * Returns a promise that resolves to a validation error or null.
 *
 * Only validates video files (MIME type starts with 'video/').
 * Bitrate is estimated as (file size in bits) / (duration in seconds).
 */
export function validateVideoBitrate(file: File): Promise<FileValidationError | null> {
  if (!file.type.startsWith('video/')) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    const url = URL.createObjectURL(file);

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);

      const durationSeconds = video.duration;
      if (!durationSeconds || durationSeconds <= 0 || !isFinite(durationSeconds)) {
        // Can't determine bitrate without duration — let it through
        resolve(null);
        return;
      }

      const bitrateMbps = (file.size * BITS_PER_BYTE) / durationSeconds / MEGABITS_DIVISOR;

      if (bitrateMbps > MAX_VIDEO_BITRATE_MBPS) {
        resolve({
          file,
          reason: 'bitrate',
          message: `We only support max bitrate ${MAX_VIDEO_BITRATE_MBPS}Mbps.`,
        });
      } else {
        resolve(null);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      // If we can't read the video, let it through — the server will reject if needed
      resolve(null);
    };

    video.src = url;
  });
}

/**
 * Validate a batch of files for image resolution and video bitrate limits.
 * Returns an object with valid files and validation errors.
 */
export async function validateFiles(files: File[]): Promise<{
  validFiles: File[];
  errors: FileValidationError[];
}> {
  const results = await Promise.all(
    files.map(async (file) => {
      const imageError = await validateImageResolution(file);
      if (imageError) return imageError;

      const videoError = await validateVideoBitrate(file);
      if (videoError) return videoError;

      return null;
    }),
  );

  const errors: FileValidationError[] = [];
  const validFiles: File[] = [];

  for (let i = 0; i < files.length; i++) {
    const error = results[i];
    if (error) {
      errors.push(error);
    } else {
      validFiles.push(files[i]);
    }
  }

  return { validFiles, errors };
}
